import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input } from '@angular/core';

import { OemMachine, Provider } from '../../../api-clients/oem-connection';
import {
  OemFieldOperation,
  OemIntegrationService,
} from '../../../services/oem-integration.service';

interface TestResult {
  ok: boolean;
  lastChecked: string;
  detail?: string;
}

/** SRA003-519 freshness states for the per-machine last-seen chip. */
export type Freshness = 'green' | 'amber' | 'grey';

// A machine seen within this window is "fresh" (green); older is amber; a null
// last_seen is grey ("No telemetry yet"). Threshold confirmed by the architect.
const FRESH_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Telemetry diagnostic panel (SRA003-404 Test 1, Epic SRA003-94).
 *
 * A DISPLAY-ONLY diagnostic placed alongside the connectivity health panel on
 * the OEM manage-view. It lets an operator (a) test connectivity, (b) list the
 * contractor's OEM equipment, and (c) trigger a test pull and inspect the
 * resulting field-operation summary records — purely to confirm telemetry is
 * flowing. None of this data feeds harvest calculations.
 *
 * Wired to the live DEV endpoints (BE #264): test connection → POST
 * /oem-connection/{provider}/test (success is status "ok"); the synchronous
 * test-pull → POST /oem-connection/{provider}/test-pull, which returns the
 * canonical summary inline as { items }; equipment uses the generated machines
 * client. Both vendors are live: Deere via field-ops, CNH via the ISO-15143-3
 * /Fleet AEMP fallback (SRA003-519, BE #437). Each item carries a `last_seen`
 * heartbeat rendered as a per-machine freshness chip; a genuinely unavailable
 * provider (503) still shows a friendly notice.
 */
@Component({
  selector: 'app-telemetry-diagnostic-panel',
  templateUrl: './telemetry-diagnostic-panel.component.html',
  styleUrls: ['./telemetry-diagnostic-panel.component.css'],
  standalone: false,
})
export class TelemetryDiagnosticPanelComponent {
  @Input() provider!: Provider;
  @Input() providerLabel = '';

  // Action 1 — Test connection
  isTesting = false;
  testResult: TestResult | null = null;

  // Action 2 — Equipment
  isLoadingEquipment = false;
  equipmentLoaded = false;
  machines: OemMachine[] = [];
  equipmentError: string | null = null;

  // Action 3 — Pull test data → field-operation summaries (returned inline)
  isPulling = false;
  operationsLoaded = false;
  operations: OemFieldOperation[] = [];
  operationsError: string | null = null;

  // Action 4 — View persisted telemetry (FE-190 Stage 2 / SRA003-427).
  // Reads real `harvester_field_operation_summaries` rows persisted by the
  // Deere harvest-data adapter (BE PR #276) — distinct from Action 3's
  // synthetic stub pull. Default window = 30 days ending now.
  isLoadingPersisted = false;
  persistedLoaded = false;
  persistedItems: OemFieldOperation[] = [];
  persistedError: string | null = null;

  constructor(private oemSvc: OemIntegrationService) {}

  testConnection(): void {
    this.isTesting = true;
    this.testResult = null;
    this.oemSvc.testConnection(this.provider).subscribe({
      next: (res) => {
        this.isTesting = false;
        this.testResult = {
          // BE returns status:"ok" (lowercase) on success — compare
          // case-insensitively so a real success doesn't render "Failed".
          ok: res?.status?.toLowerCase() === 'ok',
          lastChecked: res?.lastChecked ?? new Date().toISOString(),
          detail: res?.detail,
        };
      },
      error: (err: HttpErrorResponse) => {
        this.isTesting = false;
        this.testResult = {
          ok: false,
          lastChecked: new Date().toISOString(),
          detail: err?.message ?? 'Connection test failed',
        };
      },
    });
  }

  loadEquipment(): void {
    this.isLoadingEquipment = true;
    this.equipmentError = null;
    this.oemSvc.listMachines(this.provider).subscribe({
      next: (machines) => {
        this.isLoadingEquipment = false;
        this.equipmentLoaded = true;
        this.machines = machines ?? [];
      },
      error: (err: HttpErrorResponse) => {
        this.isLoadingEquipment = false;
        this.equipmentLoaded = true;
        this.machines = [];
        this.equipmentError = err?.message ?? 'Could not load equipment';
      },
    });
  }

  pullTestData(): void {
    this.isPulling = true;
    this.operationsLoaded = false;
    this.operationsError = null;
    // Synchronous diagnostic (SRA003-404): the response body IS the canonical
    // summary. Render it directly — there is no separate read endpoint.
    this.oemSvc.triggerTestPull(this.provider).subscribe({
      next: (res) => {
        this.isPulling = false;
        this.operationsLoaded = true;
        // Response is wrapped: { items: OemFieldOperation[] } — the shipped
        // GET /harvester-field-operations shape (SRA003-117).
        this.operations = res?.items ?? [];
      },
      error: (err: HttpErrorResponse) => {
        this.isPulling = false;
        this.operationsLoaded = true;
        this.operations = [];
        this.operationsError = this.isProviderUnavailable(err)
          ? `Test data isn't available for ${this.providerLabel || 'this provider'} yet.`
          : (err?.message ?? 'Could not pull test data');
      },
    });
  }

  /**
   * SRA003-519: per-machine freshness from the BE `last_seen` heartbeat.
   * 🟢 green = seen ≤ 24h · 🟡 amber = older · ⚪ grey = no telemetry yet
   * (null/absent/unparseable last_seen). Vendor-agnostic — both Deere and CNH
   * test-pull items carry `last_seen`.
   */
  freshness(op: OemFieldOperation): Freshness {
    if (!op.last_seen) {
      return 'grey';
    }
    const seen = Date.parse(op.last_seen);
    if (Number.isNaN(seen)) {
      return 'grey';
    }
    // A now/future stamp (incl. minor clock skew) is the freshest possible →
    // green; only a stamp older than the window is amber.
    return Date.now() - seen <= FRESH_WINDOW_MS ? 'green' : 'amber';
  }

  /** Human label for the freshness chip. */
  freshnessLabel(op: OemFieldOperation): string {
    switch (this.freshness(op)) {
      case 'green':
        return 'Fresh';
      case 'amber':
        return 'Stale';
      default:
        return 'No telemetry yet';
    }
  }

  /**
   * Load persisted canonical telemetry over a default 30-day window
   * (FE-190 Stage 2). Distinct from the synthetic diagnostic pull — these are
   * real DSS-sourced rows from `harvester_field_operation_summaries`.
   */
  loadPersistedTelemetry(): void {
    this.isLoadingPersisted = true;
    this.persistedError = null;
    this.persistedLoaded = false;
    const to = new Date();
    const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
    this.oemSvc
      .listHarvesterFieldOperations({
        from: from.toISOString(),
        to: to.toISOString(),
        provider: this.provider,
        limit: 100,
      })
      .subscribe({
        next: (res) => {
          this.isLoadingPersisted = false;
          this.persistedLoaded = true;
          this.persistedItems = res?.items ?? [];
        },
        error: (err: HttpErrorResponse) => {
          this.isLoadingPersisted = false;
          this.persistedLoaded = true;
          this.persistedItems = [];
          this.persistedError =
            err?.message ?? 'Could not load persisted telemetry';
        },
      });
  }

  /**
   * The BE returns 503 `cnh_unavailable` for a provider whose test-pull is not
   * live yet (CNH today; Deere is wired). Surface that as a friendly notice
   * rather than a generic failure.
   */
  private isProviderUnavailable(err: HttpErrorResponse): boolean {
    return err?.status === 503;
  }
}
